/**
 * 69.3b: the dropship's anchors for the sim (lane C's flight and drops), as data — measured from the model
 * (rendering/dropshipLooks.ts) and held to it by tests/dropshipLooks.test.ts. Pure numbers, no Three.js; moved here from
 * rendering/dropshipAnchors.ts (which re-exports them) when the sim began reading them (core/dropship.ts). Part of
 * src/constants.ts.
 *
 * In people (a person is 1 here; the model's units ÷ 1.8), like VEHICLE_MODEL_SIZE. The ship's frame: +z forward, x
 * across, y up; the origin on the ground under the ship when it stands on its legs (the payload slot is centred at
 * z `slotZ`, the payload's floor on the ground).
 */

/** A point in the ship's frame (people). */
export type ShipPoint = readonly [x: number, y: number, z: number];

export const DROPSHIP_ANCHORS = {
  /** the payload slot's centre along the body (its lift's centre) */
  slotZ: -0.111,
  /** the two pilots (`driver` role, inside the cab; entered by the cab's side doors) */
  pilots: [[-0.333, 2.694, 1.944], [0.333, 2.694, 1.944]] as readonly ShipPoint[],

  /** 1. the troop container's 12 seats: a seated passenger's feet, facing in across the box (`facing`: +1 faces +x) */
  seats: [
    ...[-1.306, -0.861, -0.417, 0.028, 0.472, 0.917].map(z => ({ at: [-0.517, 0.139, z] as ShipPoint, facing: 1 as const })),
    ...[-1.306, -0.861, -0.417, 0.028, 0.472, 0.917].map(z => ({ at: [0.517, 0.139, z] as ShipPoint, facing: -1 as const })),
  ],
  /** 2. out of the container: the ramp's foot on the ground (its centre) and where each seat's passenger stands once out (two files behind it), by seat index */
  rampFoot: [0, 0, -3.15] as ShipPoint,
  exits: [
    [-0.5, 0, -3.722], [0.5, 0, -3.722], [-0.5, 0, -4.278], [0.5, 0, -4.278], [-1, 0, -4.056], [1, 0, -4.056],
    [-1, 0, -4.611], [1, 0, -4.611], [-1.5, 0, -4.389], [1.5, 0, -4.389], [-1.5, 0, -4.944], [1.5, 0, -4.944],
  ] as readonly ShipPoint[],

  /**
   * 3. the container as it stands on the ground (left there after a cable drop: cover): its box (min, max) from the floor
   * to the roof, ribs and rails in; its walls (thickness) round an open back where the ramp lies down to `rampFoot`;
   * its inside (floor to ceiling) — a person seated inside is below the walls' line
   */
  container: {
    min: [-0.794, 0, -1.861] as ShipPoint, max: [0.794, 1.556, 1.611] as ShipPoint,   // (the ramp shut; open, it lies down to `rampFoot`)
    wall: 0.044, floor: 0.139, ceiling: 1.489, open: 'back' as const,
  },

  /** 4. the four clamp arms' hinges under the strongback and the four cables' tops (front pair at +z, back pair at −z) */
  clamps: [[-1.083, 2.317, -1.444], [-1.083, 2.317, 1.222], [1.083, 2.317, -1.444], [1.083, 2.317, 1.222]] as readonly ShipPoint[],
  cableTops: [[-1.017, 2.306, -1.444], [-1.017, 2.306, 1.222], [1.017, 2.306, -1.444], [1.017, 2.306, 1.222]] as readonly ShipPoint[],
  /** where the clamps take each payload (its grip's height and half-width): the container's top rails; a vehicle's is measured (its body's top less 0.18, its half-width) */
  grip: { container: { y: 1.522, x: 0.794 }, wheeled: { y: 1.089, x: 0.656 } },

  /**
   * 5. a drop on cables: the hover holds the payload's floor `hover` over the ground and pays out that much cable; each
   * cable is then (its top − the grip) + the payout long — `cableLength(grip)` (the container 2.78, the 6×6 3.22)
   */
  hover: 2.0,

  /** 6. standing on its legs: the strongback's height over the ground, the pads, and the ship's footprint (min, max; nacelles turned down for the hover / aft for cruise) for a landing-room check */
  belly: 2.361,   // (69.3b's frame fit: raised from 1.944 so a vehicle in its shipping frame hangs under it)
  pads: [[-1.336, 0, 0.417], [-1.336, 0, -1.778], [1.336, 0, 0.417], [1.336, 0, -1.778]] as readonly ShipPoint[],
  footprint: { min: [-1.922, 0, -2.917] as ShipPoint, max: [1.922, 3.494, 2.667] as ShipPoint },

  /**
   * 7. the largest payload the slot takes: along, across, up — a vehicle in its shipping frame (rendering/vehicleFrame.ts,
   * 7.6 × 3.5 × 4.0 m outside: 4.222 × 1.944 × 2.222) and a hand; so every vehicle bare or framed (the walker, 3.4 tall, does not)
   */
  vehicleMax: { length: 4.333, width: 2.056, height: 2.278 },

  /** 8. the moving parts' times (seconds) as the sheet plays them, and the cables' pace (people a second) */
  seconds: { clampsOpen: 0.6, clampsShut: 0.8, rampOpen: 1.2, rampShut: 1.0, legs: 2.0, tilt: 3.5, cableRelease: 0.5 },
  lowerRate: 0.714,
};

/** A cable's length for a payload gripped at `gripY` (people) with `payout` paid out (people; the hover's by default). */
export function cableLength(gripY: number, payout = DROPSHIP_ANCHORS.hover): number {
  return DROPSHIP_ANCHORS.cableTops[0][1] - gripY + payout;
}
